import React, { useEffect, useState } from "react";

import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import useSettings from "../../hooks/useSettings";
import useAuth from "../../hooks/useAuth.js";
import OnlyForSuperUser from "../OnlyForSuperUser";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, Business } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import CronBuilder from "../CronBuilder";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  sectionPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
    },
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
}));

export default function CrmSettings(props) {
  const { settings } = props;
  const classes = useStyles();

  const { getCurrentUserInfo } = useAuth();
  const [currentUser, setCurrentUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [showTokenCnpj, setShowTokenCnpj] = useState(false);
  const [showTokenId, setShowTokenId] = useState(false);

  const [crmInvoiceManagementType, setCrmInvoiceManagementType] = useState("");
  const [crmUrlInvoiceByCnpj, setCrmUrlInvoiceByCnpj] = useState("");
  const [crmAuthInvoiceByCnpj, setCrmAuthInvoiceByCnpj] = useState("");
  const [crmUrlInvoiceById, setCrmUrlInvoiceById] = useState("");
  const [crmAuthInvoiceById, setCrmAuthInvoiceById] = useState("");
  const [billingCheckCron, setBillingCheckCron] = useState("0 4 * * *");
  const [runningTest, setRunningTest] = useState(false);

  const { update } = useSettings();

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    getCurrentUserInfo().then((u) => {
      const userData = u.user || u;
      if (isMounted) {
        setCurrentUser(userData);
      }
    });

    if (Array.isArray(settings) && settings.length) {
      const invoiceMgmtType =
        settings.find((s) => s.key === "crmInvoiceManagementType")?.value || "";
      const urlByCnpj =
        settings.find((s) => s.key === "crmUrlInvoiceByCnpj")?.value || "";
      const authByCnpj =
        settings.find((s) => s.key === "crmAuthInvoiceByCnpj")?.value || "";
      const urlById =
        settings.find((s) => s.key === "crmUrlInvoiceById")?.value || "";
      const authById =
        settings.find((s) => s.key === "crmAuthInvoiceById")?.value || "";
      const checkCron =
        settings.find((s) => s.key === "billingCheckCron")?.value || "0 4 * * *";

      if (isMounted) {
        setCrmInvoiceManagementType(invoiceMgmtType);
        setCrmUrlInvoiceByCnpj(urlByCnpj);
        setCrmAuthInvoiceByCnpj(authByCnpj);
        setCrmUrlInvoiceById(urlById);
        setCrmAuthInvoiceById(authById);
        setBillingCheckCron(checkCron);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleSaveSetting(key, value) {
    await update({ key, value });
    toast.success(i18n.t("settings.success"));
  }

  async function handleRunBillingCheck() {
    setRunningTest(true);
    try {
      await api.post("/companies/billing-check");
      toast.success(i18n.t("crmSettings.testSuccess"));
    } catch (err) {
      toast.error(i18n.t("crmSettings.testError"));
    } finally {
      setRunningTest(false);
    }
  }

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "50px" }}
      >
        <div>Carregando configurações...</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <OnlyForSuperUser
        user={currentUser}
        yes={() => (
          <Paper className={classes.sectionPaper}>
            <div className={classes.sectionHeader}>
              <Business className={classes.sectionIcon} />
              <div>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t("crmSettings.title")}
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  {i18n.t("crmSettings.description")}
                </Typography>
              </div>
            </div>

            <Grid container spacing={3}>
              {/* Seletor de tipo - linha própria */}
              <Grid xs={12} sm={6} item>
                <FormControl
                  className={classes.formField}
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  <InputLabel id="crm-invoice-mgmt-type-label">
                    {i18n.t("crmSettings.invoiceManagementType")}
                  </InputLabel>
                  <Select
                    labelId="crm-invoice-mgmt-type-label"
                    id="crm-invoice-mgmt-type"
                    value={crmInvoiceManagementType}
                    onChange={(e) => {
                      setCrmInvoiceManagementType(e.target.value);
                      handleSaveSetting(
                        "crmInvoiceManagementType",
                        e.target.value
                      );
                    }}
                    label={i18n.t("crmSettings.invoiceManagementType")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("crmSettings.invoiceManagementNone")}</em>
                    </MenuItem>
                    <MenuItem value="perfex">
                      CRM Interno (Perfex)
                    </MenuItem>
                    <MenuItem value="asaas">
                      Asaas
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} item>
                <CronBuilder
                  value={billingCheckCron}
                  label={i18n.t("crmSettings.billingCheckTime")}
                  onChange={(cron) => {
                    setBillingCheckCron(cron);
                    handleSaveSetting("billingCheckCron", cron);
                  }}
                  onTest={crmInvoiceManagementType ? handleRunBillingCheck : undefined}
                  testRunning={runningTest}
                  testLabel={i18n.t("crmSettings.testButton")}
                />
              </Grid>

              {/* Fatura por CNPJ: URL + Token lado a lado */}
              <Grid xs={12} sm={6} item>
                <FormControl className={classes.formField} fullWidth>
                  <TextField
                    id="crm-url-invoice-cnpj"
                    label={i18n.t("crmSettings.urlInvoiceByCnpj")}
                    variant="outlined"
                    value={crmUrlInvoiceByCnpj}
                    onChange={(e) => setCrmUrlInvoiceByCnpj(e.target.value)}
                    onBlur={() =>
                      handleSaveSetting(
                        "crmUrlInvoiceByCnpj",
                        crmUrlInvoiceByCnpj
                      )
                    }
                    size="small"
                    placeholder="https://..."
                    helperText={i18n.t("crmSettings.urlInvoiceByCnpjHelper")}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} item>
                <FormControl className={classes.formField} fullWidth>
                  <TextField
                    id="crm-auth-invoice-cnpj"
                    label={i18n.t("crmSettings.authorizationByCnpj")}
                    variant="outlined"
                    type={showTokenCnpj ? "text" : "password"}
                    value={crmAuthInvoiceByCnpj}
                    onChange={(e) => setCrmAuthInvoiceByCnpj(e.target.value)}
                    onBlur={() =>
                      handleSaveSetting(
                        "crmAuthInvoiceByCnpj",
                        crmAuthInvoiceByCnpj
                      )
                    }
                    size="small"
                    placeholder="Bearer xxxxxxxx"
                    helperText={i18n.t("crmSettings.authorizationHelper")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowTokenCnpj(!showTokenCnpj)}
                            edge="end"
                          >
                            {showTokenCnpj ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Fatura por ID: URL + Token lado a lado */}
              <Grid xs={12} sm={6} item>
                <FormControl className={classes.formField} fullWidth>
                  <TextField
                    id="crm-url-invoice-id"
                    label={i18n.t("crmSettings.urlInvoiceById")}
                    variant="outlined"
                    value={crmUrlInvoiceById}
                    onChange={(e) => setCrmUrlInvoiceById(e.target.value)}
                    onBlur={() =>
                      handleSaveSetting(
                        "crmUrlInvoiceById",
                        crmUrlInvoiceById
                      )
                    }
                    size="small"
                    placeholder="https://..."
                    helperText={i18n.t("crmSettings.urlInvoiceByIdHelper")}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} item>
                <FormControl className={classes.formField} fullWidth>
                  <TextField
                    id="crm-auth-invoice-id"
                    label={i18n.t("crmSettings.authorizationById")}
                    variant="outlined"
                    type={showTokenId ? "text" : "password"}
                    value={crmAuthInvoiceById}
                    onChange={(e) => setCrmAuthInvoiceById(e.target.value)}
                    onBlur={() =>
                      handleSaveSetting(
                        "crmAuthInvoiceById",
                        crmAuthInvoiceById
                      )
                    }
                    size="small"
                    placeholder="Bearer xxxxxxxx"
                    helperText={i18n.t("crmSettings.authorizationHelper")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowTokenId(!showTokenId)}
                            edge="end"
                          >
                            {showTokenId ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )}
      />
    </div>
  );
}
